import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Wallet, TrendingUp, TrendingDown, AlertTriangle, 
  RefreshCw, Settings, Bell, ArrowUpRight, ArrowDownRight,
  Calendar, PiggyBank, Loader2, CheckCircle2, XCircle
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

var API_URL = process.env.REACT_APP_BACKEND_URL;

function TresoreriePage() {
  var { token } = useAuth();
  var [loading, setLoading] = useState(true);
  var [dashboard, setDashboard] = useState(null);
  var [alertes, setAlertes] = useState([]);
  var [parametres, setParametres] = useState({
    seuil_minimum: 1000,
    alerte_charges: true,
    jours_prevision: 90
  });
  var [savingParams, setSavingParams] = useState(false);
  var [annee] = useState(new Date().getFullYear());

  var fetchData = useCallback(async function() {
    setLoading(true);
    try {
      var [dashboardRes, alertesRes] = await Promise.all([
        axios.get(API_URL + '/api/tresorerie/dashboard?annee=' + annee, {
          headers: { Authorization: 'Bearer ' + token }
        }),
        axios.get(API_URL + '/api/tresorerie/alertes', {
          headers: { Authorization: 'Bearer ' + token }
        })
      ]);
      
      setDashboard(dashboardRes.data);
      setAlertes(alertesRes.data.alertes || []);
      setParametres(alertesRes.data.parametres || {
        seuil_minimum: 1000,
        alerte_charges: true,
        jours_prevision: 90
      });
    } catch (error) {
      console.error('Erreur chargement trésorerie:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [token, annee]);

  useEffect(function() {
    fetchData();
  }, [fetchData]);

  var handleSaveParametres = async function() {
    setSavingParams(true);
    try {
      await axios.post(API_URL + '/api/tresorerie/parametres', parametres, {
        headers: { Authorization: 'Bearer ' + token }
      });
      toast.success('Paramètres enregistrés');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingParams(false);
    }
  };

  var formatMontant = function(montant) {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montant || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  var alertesHautes = alertes.filter(function(a) { return a.priorite === 'haute'; });
  var alertesMoyennes = alertes.filter(function(a) { return a.priorite === 'moyenne'; });

  return (
    <div className="space-y-6" data-testid="tresorerie-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestion de Trésorerie</h1>
          <p className="text-slate-500">Suivez vos flux et anticipez vos besoins</p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-tresorerie">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Solde actuel</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatMontant(dashboard?.solde_actuel)}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-full">
                <Wallet className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Flux mensuel moyen</p>
                <p className={`text-2xl font-bold ${dashboard?.flux_mensuel_moyen >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {dashboard?.flux_mensuel_moyen >= 0 ? '+' : ''}{formatMontant(dashboard?.flux_mensuel_moyen)}
                </p>
              </div>
              <div className={`p-3 rounded-full ${dashboard?.flux_mensuel_moyen >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                {dashboard?.flux_mensuel_moyen >= 0 ? (
                  <TrendingUp className="h-6 w-6 text-emerald-600" />
                ) : (
                  <TrendingDown className="h-6 w-6 text-red-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Charges URSSAF estimées</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatMontant(dashboard?.charges_estimees?.prochain_paiement)}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <PiggyBank className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${alertesHautes.length > 0 ? 'border-l-red-500' : 'border-l-green-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Alertes actives</p>
                <p className="text-2xl font-bold text-slate-900">
                  {alertes.length}
                </p>
              </div>
              <div className={`p-3 rounded-full ${alertesHautes.length > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                {alertesHautes.length > 0 ? (
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                ) : (
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alertes urgentes */}
      {alertesHautes.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Alertes urgentes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {alertesHautes.map(function(alerte, index) {
              return (
                <div key={index} className="p-3 bg-white rounded-lg border border-red-200">
                  <p className="font-medium text-red-800">{alerte.titre}</p>
                  <p className="text-sm text-red-600">{alerte.message}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    <strong>Action recommandée:</strong> {alerte.action}
                  </p>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="previsions" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="previsions">Prévisions</TabsTrigger>
          <TabsTrigger value="historique">Historique</TabsTrigger>
          <TabsTrigger value="alertes">Alertes ({alertes.length})</TabsTrigger>
          <TabsTrigger value="parametres">Paramètres</TabsTrigger>
        </TabsList>

        {/* Onglet Prévisions */}
        <TabsContent value="previsions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Prévisions de trésorerie (6 mois)</CardTitle>
              <CardDescription>
                Basées sur vos flux historiques
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dashboard?.previsions || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mois_nom" />
                    <YAxis tickFormatter={function(v) { return v + ' €'; }} />
                    <Tooltip 
                      formatter={function(value) { return formatMontant(value); }}
                      labelStyle={{ color: '#1e293b' }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="solde_prevu" 
                      name="Solde prévu"
                      fill="#10b981" 
                      fillOpacity={0.3}
                      stroke="#10b981" 
                      strokeWidth={2}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="recettes_prevues" 
                      name="Recettes"
                      fill="#3b82f6" 
                      fillOpacity={0.2}
                      stroke="#3b82f6" 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="depenses_prevues" 
                      name="Dépenses"
                      fill="#ef4444" 
                      fillOpacity={0.2}
                      stroke="#ef4444" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Tableau des prévisions */}
          <Card>
            <CardHeader>
              <CardTitle>Détail des prévisions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 text-sm font-medium text-slate-500">Mois</th>
                      <th className="text-right py-2 px-3 text-sm font-medium text-slate-500">Recettes</th>
                      <th className="text-right py-2 px-3 text-sm font-medium text-slate-500">Dépenses</th>
                      <th className="text-right py-2 px-3 text-sm font-medium text-slate-500">Flux</th>
                      <th className="text-right py-2 px-3 text-sm font-medium text-slate-500">Solde prévu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dashboard?.previsions || []).map(function(prev, index) {
                      return (
                        <tr key={index} className="border-b hover:bg-slate-50">
                          <td className="py-2 px-3 font-medium">{prev.mois_nom}</td>
                          <td className="py-2 px-3 text-right text-emerald-600">
                            +{formatMontant(prev.recettes_prevues)}
                          </td>
                          <td className="py-2 px-3 text-right text-red-600">
                            -{formatMontant(prev.depenses_prevues)}
                          </td>
                          <td className={`py-2 px-3 text-right font-medium ${prev.flux_prevu >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {prev.flux_prevu >= 0 ? '+' : ''}{formatMontant(prev.flux_prevu)}
                          </td>
                          <td className={`py-2 px-3 text-right font-bold ${prev.solde_prevu >= 0 ? 'text-slate-900' : 'text-red-600'}`}>
                            {formatMontant(prev.solde_prevu)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Historique */}
        <TabsContent value="historique" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Historique {annee}</CardTitle>
              <CardDescription>
                Évolution mensuelle de votre trésorerie
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dashboard?.historique || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mois_nom" />
                    <YAxis tickFormatter={function(v) { return v + ' €'; }} />
                    <Tooltip formatter={function(value) { return formatMontant(value); }} />
                    <Legend />
                    <Bar dataKey="recettes" name="Recettes" fill="#10b981" />
                    <Bar dataKey="depenses" name="Dépenses" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Graphique du solde cumulé */}
          <Card>
            <CardHeader>
              <CardTitle>Évolution du solde</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dashboard?.historique || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mois_nom" />
                    <YAxis tickFormatter={function(v) { return v + ' €'; }} />
                    <Tooltip formatter={function(value) { return formatMontant(value); }} />
                    <Line 
                      type="monotone" 
                      dataKey="solde_cumule" 
                      name="Solde cumulé"
                      stroke="#3b82f6" 
                      strokeWidth={3}
                      dot={{ fill: '#3b82f6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Alertes */}
        <TabsContent value="alertes" className="space-y-4">
          {alertes.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">Aucune alerte</h3>
                <p className="text-slate-500">Votre trésorerie est en bonne santé</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {alertes.map(function(alerte, index) {
                var priorityColors = {
                  haute: 'border-red-500 bg-red-50',
                  moyenne: 'border-yellow-500 bg-yellow-50',
                  basse: 'border-blue-500 bg-blue-50'
                };
                var iconColors = {
                  haute: 'text-red-600',
                  moyenne: 'text-yellow-600',
                  basse: 'text-blue-600'
                };
                
                return (
                  <Card key={index} className={`border-l-4 ${priorityColors[alerte.priorite]}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-full ${alerte.priorite === 'haute' ? 'bg-red-100' : alerte.priorite === 'moyenne' ? 'bg-yellow-100' : 'bg-blue-100'}`}>
                          {alerte.priorite === 'haute' ? (
                            <XCircle className={`h-5 w-5 ${iconColors[alerte.priorite]}`} />
                          ) : (
                            <AlertTriangle className={`h-5 w-5 ${iconColors[alerte.priorite]}`} />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-slate-900">{alerte.titre}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              alerte.priorite === 'haute' ? 'bg-red-200 text-red-800' :
                              alerte.priorite === 'moyenne' ? 'bg-yellow-200 text-yellow-800' :
                              'bg-blue-200 text-blue-800'
                            }`}>
                              {alerte.priorite}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{alerte.message}</p>
                          <p className="text-sm text-slate-500 mt-2">
                            <strong>Action:</strong> {alerte.action}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Onglet Paramètres */}
        <TabsContent value="parametres">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Paramètres d'alertes
              </CardTitle>
              <CardDescription>
                Configurez vos seuils et notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="seuil">Seuil minimum de trésorerie (€)</Label>
                <Input
                  id="seuil"
                  type="number"
                  value={parametres.seuil_minimum}
                  onChange={function(e) {
                    setParametres({...parametres, seuil_minimum: parseFloat(e.target.value) || 0});
                  }}
                  placeholder="1000"
                />
                <p className="text-xs text-slate-500">
                  Vous serez alerté si votre solde passe sous ce montant
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Alertes charges URSSAF</Label>
                  <p className="text-xs text-slate-500">
                    Rappels avant les échéances trimestrielles
                  </p>
                </div>
                <Switch
                  checked={parametres.alerte_charges}
                  onCheckedChange={function(checked) {
                    setParametres({...parametres, alerte_charges: checked});
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="jours">Horizon de prévision (jours)</Label>
                <Input
                  id="jours"
                  type="number"
                  value={parametres.jours_prevision}
                  onChange={function(e) {
                    setParametres({...parametres, jours_prevision: parseInt(e.target.value) || 90});
                  }}
                  placeholder="90"
                />
              </div>

              <Button onClick={handleSaveParametres} disabled={savingParams}>
                {savingParams ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default TresoreriePage;
